
import React, { useRef, useState, useEffect } from "react";
import l from "components/util/l";
import { getNodeContent } from "components/util/node_util";
import { renderHTML } from "components/util/render_util";
import HelpMessage from "components/util/helpers/HelpMessage";
import ConversationAttachment from "./Conversation.sub.attachment.view";

const ConversationMessage = ({
  id,
  action,
  chatSettings,
  content,
  lastMessage,
  messageSettings,
  models,
  nextMessage,
  prevMessage,
  type,
  isHighlighted,
  onClickRegenerateMessage,
  onClickRepeatExecution,
}) => {

  const chatModel = chatSettings?.model ?? 'OpenAI GPT-4o mini';
  const messageModel = messageSettings?.llm_model ?? chatModel;
  const messageModelLabel = models.find(model => model.name == messageModel)?.publicName ?? messageModel;

  const text = l(content.text);
  const plain = l(content.plain);
  const attachments = content.attachments;

  const messageRef = useRef(null);
  const [hasCopied, setHasCopied] = useState(false);
  const [dropdownData, setDropdownData] = useState(null);

  const onClickCopy = (e) => {

    navigator.clipboard.writeText(plain || text);
    setHasCopied(true);

    const y = (e.clientY || e.pageY);
    const x = (e.clientX || e.pageX) + (type == "BOT_MESSAGE" ? -125 : 20);

    const messageDiv = document.createElement('div');
    messageDiv.innerText = l("Texto copiado%PA009");
    messageDiv.style.position = 'fixed';
    messageDiv.style.top = `${y}px`;
    messageDiv.style.left = `${x}px`;
    messageDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
    messageDiv.style.color = 'white';
    messageDiv.style.padding = '5px 10px';

    document.body.appendChild(messageDiv);
    setTimeout(() => {
      document.body.removeChild(messageDiv);
    }, 1200);

  }

  const onClickOpenRegenerateOptions = (e) => {

    e.preventDefault();
    e.stopPropagation();

    let x = (e.clientX || e.pageX) + 20;
    let dropdownHeight = Math.min(models.length * 45, 320);

    let y = (e.clientY || e.pageY);
    if (y + dropdownHeight > window.innerHeight)
      y = window.innerHeight - dropdownHeight - 10;

    setDropdownData({ x, y });

  }

  const onClickRegenerate = (message, model) => {
    setDropdownData(null);
    onClickRegenerateMessage(message, model);
  }

  useEffect(() => {
    setTimeout(() => {
      let variables = document.getElementsByClassName("variable")
      for (let variable of variables)
        try {
          variable.textContent = variable.getAttribute("data-id")
        } catch { }
    }, 100);
  }, [])

  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        setHasCopied(false);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [hasCopied]);

  useEffect(() => {

    const handleClickOutside = (event) => {
      if (messageRef.current && !messageRef.current.contains(event.target)) {
        setDropdownData(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);

  }, [messageRef]);

  if (!text?.replaceAll("<p></p>", "").trim() && (!attachments || attachments.length == 0))
    return "";

  return (<>

    <div
      id={`chat-message-${id}`}
      className="w-100"
    >
      <div
        className={
          `message-wrapper d-flex w-auto position-relative w-100
          ${type == "BOT_MESSAGE" ? "flex-row" : "flex-row-reverse user"}
          ${type == "BOT_MESSAGE" ? "animate__animated animate__fadeIn animate__faster" : ""}
          mx-0 mx-sm-2`
        }>

        <div
          className={`message ${type == "BOT_MESSAGE" ? "" : "user"}
            ${type == "BOT_MESSAGE" ? "order-1" : "order-2"} ${isHighlighted ? "highlighted-message" : ""}`}
          style={{
            maxWidth: window.innerWidth > 576 ?
              (plain?.length > 120 ? (4 * Math.log(plain?.length - 120) + 66) + "%" : "66%") :
              (plain?.length > 120 ? (4 * Math.log(plain?.length - 120) + 75) + "%" : "75%"),
            marginTop: window.innerWidth > 576 ?
              (prevMessage?.type != type ? "0.7rem" : "0.25rem") :
              (prevMessage?.type != type ? "0.4rem" : "0.12rem"),
            marginBottom: window.innerWidth > 576 ?
              (nextMessage?.type != type ? "0.45rem" : "0.25rem") :
              (nextMessage?.type != type ? "0.15rem" : "0.12rem"),
            zIndex: 1,
          }}>
          
          { type == "BOT_MESSAGE" &&
            <label className="sender-name font-weight-bold w-100">
              {chatSettings?.agent_name?.trim() || "Workix"}
            </label>
           }
           
          {
            text.includes("%PA") ? l(text.replaceAll("<p>", "").replaceAll("</p>", "")) :
              renderHTML(
                action?.node && action?.node?.action?.type == "TRIGGER" && !action?.is_form
                  ? getNodeContent(action.node?.action?.ui_settings?.node, action?.node?.input_values,
                    "execution_message", action.node?.action?.ui_settings?.form)
                  : text)
          }

          {
            attachments && attachments.length > 0 &&
            <div className="d-flex flex-column">
              {
                function () {
                  return attachments.map((attachment, index) => {
                    return <ConversationAttachment 
                      key={`attachment-view-${index}`} 
                      attachment={attachment} 
                      index={index} 
                    />
                  });
                }()
              }
            </div>
          }

        </div>

        {
          function () {
            const showRegenerateOption = messageSettings?.is_open_response ?? false
            const showRepeateOption = messageSettings?.is_execution_response ?? false

            return (
              <div className={`d-none d-md-block ${type == "BOT_MESSAGE" ? "order-2" : "order-1"}`}>
                <div className={`message-options d-flex align-items-start h-auto
                ${plain.length < 120 && (showRegenerateOption || showRepeateOption) ? "flex-wrap" : "flex-column"}`}>

                  <HelpMessage
                    message={l("Copiar mensaje%PA950")}
                    placement={window.innerWidth > 576 && plain.length > 120 ? "right" : "bottom"} >
                    <button
                      type="button"
                      className="btn btn-link p-0 mb-2 mr-2"
                      onClick={onClickCopy}>
                      {
                        hasCopied
                          ? <i className="fas fa-lg fa-check" />
                          : <i className="fas fa-lg fa-copy" />
                      }
                    </button>
                  </HelpMessage>

                  {showRegenerateOption &&
                    <HelpMessage
                      message={l("Volver a generar respuesta%PA008")}
                      placement={window.innerWidth > 576 && plain.length > 120 ? "right" : "bottom"} >
                      <button
                        className="btn btn-link p-0 mb-2 mr-2"
                        onClick={onClickOpenRegenerateOptions}>
                        <i className="fas fa-lg fa-sync" />
                      </button>
                    </HelpMessage>}

                  {showRepeateOption &&
                    <HelpMessage
                      message="Repetir ejecución"
                      placement={window.innerWidth > 576 && plain.length > 120 ? "right" : "bottom"}>
                      <button
                        className="btn btn-link p-0 mb-2 mr-2"
                        onClick={() => onClickRepeatExecution(id)} >
                        <i className="fas fa-lg fa-play" />
                      </button>
                    </HelpMessage>}

                </div>
              </div>
            )
          }()
        }

      </div>
    </div>



    {
      dropdownData &&
      <div
        ref={messageRef}
        class="dropdown-menu show"
        aria-labelledby="regenerate-options"
        style={{
          zIndex: 10,
          position: "fixed",
          top: `${dropdownData.y}px`,
          left: `${dropdownData.x}px`,
          maxHeight: "320px",
          overflowY: "auto",
        }}
      >
        <h6 className="dropdown-header">{l("Modelo%PA007")}</h6>
        {
          models.map((model) =>
            model.name == messageModel ? null :
              <a href="#" className="dropdown-item" key={`model-${model.name}`} onClick={() => onClickRegenerate(lastMessage, model.name)}>
                <i className={`text-primary mr-2 ${getModelIcon(messageModel, model.name)}`} />
                {model.publicName}
              </a>
          )
        }
        <div className="dropdown-divider"></div>
        <a href="#" className="dropdown-item" onClick={() => onClickRegenerate(lastMessage, messageModel)}>
          <i className={`text-primary mr-2 ${getModelIcon(messageModel)}`} />
          {messageModelLabel}
        </a>
      </div>
    }

  </>);

}

const getModelIcon = (defaultModel, model = null) => {
  if (!model) return "fas fa-sync";
  if (model.includes("OpenAI o")) return "fas fa-brain";
  if (model.includes("OpenAI GPT")) return "fas fa-sun";
  if (model.includes("DeepSeek")) return "fas fa-microchip";
  if (model.includes("Gemini")) return "fas fa-magic";
}

export default ConversationMessage;
